import { io } from 'socket.io-client';

/**
 * Generic WebSocket Helper
 * Provides a reusable, configurable WebSocket connection manager
 */

/**
 * Creates a generic WebSocket manager instance
 * @param {Object} config - Configuration options
 * @param {string} config.url - WebSocket server URL
 * @param {Object} config.socketOptions - Socket.IO client options
 * @param {Object} config.events - Socket event names (CONNECT, DISCONNECT, etc.)
 * @param {Function} config.onConnect - Callback when connected
 * @param {Function} config.onDisconnect - Callback when disconnected
 * @param {Function} config.onError - Callback on connection error
 * @param {Object} config.heartbeat - Heartbeat configuration
 * @param {string} config.heartbeat.event - Heartbeat event name
 * @param {number} config.heartbeat.interval - Heartbeat interval in ms
 * @param {Function} config.heartbeat.getPayload - Function to get heartbeat payload
 * @param {number} config.maxReconnectionAttempts - Maximum reconnection attempts
 * @returns {Object} WebSocket manager instance
 */
export const createWebSocketManager = config => {
  const {
    url,
    socketOptions = {},
    events = {},
    onConnect,
    onDisconnect,
    onError,
    heartbeat = null,
    maxReconnectionAttempts = 5,
  } = config;

  let socket = null;
  let listeners = new Map();
  let subscribedEvents = new Set(); // Track which events we've subscribed to on the socket
  let reconnectionAttempts = 0;
  let isInitializing = false;
  let heartbeatTimer = null;

  // Default socket options
  const defaultSocketOptions = {
    withCredentials: true,
    reconnection: true,
    reconnectionAttempts: maxReconnectionAttempts,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    timeout: 20000,
    transports: ['websocket', 'polling'],
    ...socketOptions,
  };

  // Default event names
  const defaultEvents = {
    CONNECT: 'connect',
    DISCONNECT: 'disconnect',
    CONNECT_ERROR: 'connect_error',
    ...events,
  };

  /* -------------------- Internal Helpers -------------------- */

  const emitToListeners = (eventName, data) => {
    if (!listeners.has(eventName)) return;
    listeners.get(eventName).forEach(cb => {
      try {
        cb(data);
      } catch (err) {
        console.error(`Error in ${eventName} listener`, err);
      }
    });
  };

  const startHeartbeat = () => {
    if (!heartbeat || heartbeatTimer) return;

    heartbeatTimer = setInterval(() => {
      if (socket?.connected) {
        const payload = heartbeat.getPayload
          ? heartbeat.getPayload()
          : { timestamp: Date.now() };
        socket.emit(heartbeat.event, payload);
      }
    }, heartbeat.interval || 10000);
  };

  const stopHeartbeat = () => {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  };

  /* -------------------- Socket Handlers -------------------- */

  const setupEventHandlers = () => {
    if (!socket) return;

    socket.on(defaultEvents.CONNECT, () => {
      reconnectionAttempts = 0;
      console.log('Connected to WebSocket server');
      emitToListeners('connection-status', { connected: true });

      // Re-subscribe to any events that were registered before connection
      listeners.forEach((callbacks, eventName) => {
        if (!subscribedEvents.has(eventName)) {
          socket.on(eventName, data => {
            emitToListeners(eventName, data);
          });
          subscribedEvents.add(eventName);
        }
      });

      if (heartbeat) {
        startHeartbeat();
      }

      if (onConnect) {
        onConnect(socket);
      }
    });

    socket.on(defaultEvents.DISCONNECT, reason => {
      console.log('Disconnected:', reason);
      emitToListeners('connection-status', { connected: false });
      stopHeartbeat();

      if (onDisconnect) {
        onDisconnect(reason);
      }
    });

    socket.on(defaultEvents.CONNECT_ERROR, error => {
      console.error('Connection error:', error);
      reconnectionAttempts++;

      if (reconnectionAttempts >= maxReconnectionAttempts) {
        emitToListeners('connection-error', {
          error: 'Unable to connect',
          attempts: reconnectionAttempts,
        });
      }

      if (onError) {
        onError(error);
      }
    });
  };

  /* -------------------- Public API -------------------- */

  const initialize = (customUrl = null) => {
    const targetUrl = customUrl || url;
    if (!targetUrl) {
      console.error('WebSocket URL is required');
      return null;
    }

    if (socket?.connected || isInitializing) return socket;

    isInitializing = true;

    socket = io(targetUrl, defaultSocketOptions);
    setupEventHandlers();

    isInitializing = false;
    return socket;
  };

  const emitEvent = (eventName, data) => {
    if (!socket?.connected) return false;
    socket.emit(eventName, data);
    return true;
  };

  const on = (eventName, callback) => {
    if (!listeners.has(eventName)) {
      listeners.set(eventName, []);
    }
    listeners.get(eventName).push(callback);

    // Subscribe to socket event if not already subscribed
    if (socket && !subscribedEvents.has(eventName)) {
      socket.on(eventName, data => {
        emitToListeners(eventName, data);
      });
      subscribedEvents.add(eventName);
    }

    return () => off(eventName, callback);
  };

  const off = (eventName, callback) => {
    if (!listeners.has(eventName)) return;

    const callbacks = listeners.get(eventName);
    const filtered = callbacks.filter(cb => cb !== callback);

    if (filtered.length === 0) {
      listeners.delete(eventName);
      if (socket && subscribedEvents.has(eventName)) {
        socket.off(eventName);
        subscribedEvents.delete(eventName);
      }
    } else {
      listeners.set(eventName, filtered);
    }
  };

  const disconnect = () => {
    if (!socket) return;

    stopHeartbeat();
    socket.disconnect();

    socket = null;
    listeners.clear();
    subscribedEvents.clear();
    reconnectionAttempts = 0;
  };

  const reconnect = () => {
    if (socket) {
      socket.connect();
    } else {
      initialize();
    }
  };

  const isConnected = () => Boolean(socket?.connected);

  const getSocketId = () => socket?.id || null;

  const sendHeartbeat = () => {
    if (!socket?.connected || !heartbeat) return false;
    const payload = heartbeat.getPayload
      ? heartbeat.getPayload()
      : { timestamp: Date.now() };
    socket.emit(heartbeat.event, payload);
    return true;
  };

  const getSocket = () => socket;

  return {
    initialize,
    emitEvent,
    on,
    off,
    disconnect,
    reconnect,
    isConnected,
    getSocketId,
    sendHeartbeat,
    stopHeartbeat,
    getSocket,
  };
};
